/* eslint-disable react-refresh/only-export-components */
import { lazy } from "react"
import { Navigate, Outlet, createBrowserRouter } from "react-router"

import queryClient from "@/api/queryClient"
import ErrorComponent from "@/components/error"
import CompactLayout from "@/layouts/compact"
import LoginLayout from "@/layouts/login/LoginLayout"
import MainLayout from "@/layouts/main"
import Page500 from "@/pages/Page500"
import { loader as modelLoader } from "@/pages/item/loader"
import ItemsPage from "@/pages/items/component"
import * as Page from "@/routes/pages"
import ThrowError from "@/utils/throwError"

import { AdminProviders, MainProviders, RootProviders } from "./providers"

const LazyAdminLayout = lazy(() => import("@/layouts/admin"))

const router = createBrowserRouter(
  [
    {
      path: "/",
      element: (
        <RootProviders>
          <Outlet />
        </RootProviders>
      ),
      errorElement: import.meta.env.PROD ? <Page500 /> : undefined,
      children: [
        {
          path: "auth",
          element: <LoginLayout />,
          children: [
            {
              index: true,
              element: <Navigate to="/auth/login" replace />,
            },
            {
              path: "login",
              element: <Page.Login />,
            },
            {
              path: "callback",
              children: [
                {
                  path: ":provider",
                  element: <Page.CallBack />,
                },
              ],
            },
            {
              path: "logout",
              element: <Page.Logout />,
            },
            {
              path: "signup",
              element: <Page.Signup />,
            },
            {
              path: "auth0login",
              element: <Page.Auth0Login />,
            },
          ],
        },
        {
          path: "/",
          element: (
            <MainProviders>
              <MainLayout />
            </MainProviders>
          ),
          children: [
            {
              index: true,
              element: <ItemsPage />,
            },
            {
              path: "debug",
              element: <Page.Root />,
            },
            {
              path: "item",
              children: [
                {
                  index: true,
                  element: <Navigate to="/" replace />,
                },
                {
                  path: ":modelid",
                  loader: modelLoader(queryClient),
                  element: <Page.Item />,
                  errorElement: <ErrorComponent />,
                },
              ],
            },
            {
              path: "info",
              children: [
                {
                  index: true,
                  element: <Navigate to="/info/brands" replace />,
                },
                {
                  path: "brands",
                  element: <Page.Brands />,
                },
                {
                  path: "delivery",
                  element: <Page.Delivery />,
                },
                {
                  path: "usps",
                  element: <Page.Usps />,
                },
              ],
            },
            {
              path: "colors",
              children: [
                {
                  index: true,
                  element: <Navigate to="/" replace />,
                },
                {
                  path: "fermob",
                  element: <Page.Fermob />,
                },
                {
                  path: "coatings",
                  element: <Page.Coatings />,
                },
              ],
            },
            {
              path: "termsofdelivery", // Link from old pricelist
              element: <Navigate to="/info/delivery" replace />,
            },
            {
              path: "reset-password", // Link from old pricelist
              element: <Navigate to="/" replace />,
            },
            {
              path: "confirm-password", // Link from old pricelist
              element: <Navigate to="/auth/login" replace />,
            },
            {
              path: "login", // Link from old pricelist
              element: <Navigate to="/auth/login" replace />,
            },
            {
              path: "fabrics",
              children: [
                {
                  index: true,
                  element: <Navigate to="/fabrics/prices" replace />,
                },
                {
                  path: "prices",
                  element: <Page.FabricPrices />,
                },
                {
                  path: "info",
                  element: <Page.FabricInfo />,
                },
                {
                  path: "overview",
                  element: <Page.FabricOverview />,
                },
                {
                  path: "outdoor-stock",
                  element: <Page.OutdoorStock />,
                },
              ],
            },
            {
              path: "aanvullendeinformatie", // Links from old pricelist
              children: [
                {
                  index: true,
                  element: <Navigate to="/info/brands" replace />,
                },
                {
                  path: "brands",
                  element: <Navigate to="/info/brands" replace />,
                },
                {
                  path: "stof_en_leer",
                  element: <Navigate to="/fabrics/prices" replace />,
                },
              ],
            },
            {
              path: "kleurenoverzicht", // Links from old pricelist
              children: [
                {
                  index: true,
                  element: <Navigate to="/colors/fermob" replace />,
                },
                {
                  path: "fermob",
                  element: <Navigate to="/colors/fermob" replace />,
                },
                {
                  path: "coating",
                  element: <Navigate to="/colors/coatings" replace />,
                },
              ],
            },
          ],
        },
        {
          path: "admin",
          element: (
            <AdminProviders>
              <LazyAdminLayout />
            </AdminProviders>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="/admin/model/list" replace />,
            },
            {
              path: "model",
              children: [
                {
                  index: true,
                  element: <Navigate to="/admin/model/list" replace />,
                },
                {
                  path: ":modelid",
                  element: <Page.AdminModelEdit />,
                  errorElement: <ErrorComponent />,
                },
                {
                  path: "list",
                  element: <Page.AdminModelList />,
                },
                {
                  path: "todos",
                  errorElement: <ErrorComponent />,
                  children: [
                    {
                      index: true,
                      element: <Page.AdminModelTodos />,
                    },
                    {
                      path: ":modelid",
                      element: <Page.AdminModelTodos />,
                    },
                  ],
                },
                {
                  path: "missing-properties",
                  errorElement: <ErrorComponent />,
                  children: [
                    {
                      index: true,
                      element: <Page.AdminModelMissingProps />,
                    },
                    {
                      path: ":modelid",
                      element: <Page.AdminModelMissingProps />,
                    },
                  ],
                },
              ],
            },
            {
              path: "property",
              children: [
                {
                  index: true,
                  element: <Navigate to="/admin/property/list" replace />,
                },
                {
                  path: "list",
                  element: <Page.AdminPropertyList />,
                },
                {
                  path: "create",
                  element: <Page.AdminPropertyCreate />,
                },
              ],
            },
            {
              path: "associations",
              children: [
                {
                  index: true,
                  element: <Navigate to="/admin/associations/list" replace />,
                },
                {
                  path: "list",
                  element: <Page.AdminAssociationList />,
                },
              ],
            },
          ],
        },
        {
          path: "500",
          element: <Page.ServerError />,
        },
        {
          path: "*",
          element: <CompactLayout />,
          children: [
            {
              path: "404",
              element: <Page.NotFound />,
            },
            {
              path: "500",
              element: <Page.ServerError />,
            },
            {
              path: "401",
              element: <Page.Unauthorized />,
            },
            {
              path: "error",
              element: <ThrowError />,
            },
            {
              path: "*",
              element: <Navigate to="/404" replace />,
            },
          ],
        },
      ],
    },
  ],
  {}
)

export default router
